import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AvatarSlot,
  BoardContainer,
  BoardHeader,
  FinalHeader,
  Medal,
  PlayerName,
  PlayerRow,
  Points,
} from "./Board.styles";
import { useGameContext } from "../../contexts/GameContext";
import { useSessionContext } from "../../contexts/SessionContext";
import {
  Badge,
  Button,
  CircularProgress,
  Fab,
  IconButton,
  Zoom,
} from "@mui/material";
import { toast } from "material-react-toastify";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../routes";
import AnimalBg from "../AnimalBg/AnimalBg";
import { AnimalsEnum } from "../AnimalBg/types";
import Icon from "../ui/Icon";
import Tag from "../ui/Tag";
import Confetti from "../ui/Confetti";
import ScoreChart from "./ScoreChart";

const Board: React.FC = () => {
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  const currentPlayers = useRef<{ [id: string]: string }>({});

  const {
    players,
    tableAnswers,
    votingStarted: voteStarted,
    finished,
    onlinePlayers,
  } = useGameContext();
  const { user_id } = useSessionContext();

  const userVotes = useMemo(() => {
    const allVotes: any = {};

    tableAnswers.forEach(({ votes }) => {
      votes?.forEach((user) => {
        allVotes[user] = true;
      });
    });

    return allVotes;
  }, [tableAnswers]);

  const userAnswers = useMemo(() => {
    const allAnswers: any = {};
    tableAnswers.forEach(({ user_id }) => (allAnswers[user_id || ""] = true));
    return allAnswers;
  }, [tableAnswers]);

  useEffect(() => {
    const currentIds = Object.keys(currentPlayers.current);
    for (const { id, name } of onlinePlayers) {
      if (id) {
        if (
          currentIds.length &&
          id !== user_id &&
          !currentPlayers.current[id]
        ) {
          toast.success(`${name} entrou na sala.`);
        }
        currentPlayers.current[id] = name || "";
      }
    }
    currentIds.forEach((id) => {
      if (!onlinePlayers.find((player) => id === player.id)) {
        if (id !== user_id) {
          toast.error(`${currentPlayers.current[id]} saiu sala.`);
        }
        delete currentPlayers.current[id];
      }
    });
  }, [onlinePlayers, user_id]);

  const ranking = useMemo(
    () => players.sort((a, b) => (b.points || 0) - (a.points || 0)),
    [players],
  );

  const isDone = (id?: string) =>
    (voteStarted && userVotes[id || ""]) ||
    (!voteStarted && userAnswers[id || ""]);

  return (
    <BoardContainer $open={open || finished} $finished={finished}>
      {finished && <Confetti count={120} duration={2200} />}

      {!finished && (
        <Zoom
          in={!open}
          timeout={100}
          unmountOnExit
          style={{
            transitionDelay: "200ms",
          }}
        >
          <Fab
            color="inherit"
            onClick={() => setOpen(true)}
            size="medium"
            aria-label="Jogadores"
          >
            <Badge
              variant="standard"
              color="info"
              badgeContent={onlinePlayers.length}
              style={{ cursor: "pointer" }}
            >
              <Icon name="users" size={22} />
            </Badge>
          </Fab>
        </Zoom>
      )}

      {!finished && open && (
        <BoardHeader>
          <h2>
            <Icon name="users" />
            Jogadores
            <Tag $tone="teal">{onlinePlayers.length} online</Tag>
          </h2>

          <IconButton onClick={() => setOpen(false)} aria-label="Fechar">
            <Icon name="close" />
          </IconButton>
        </BoardHeader>
      )}

      {finished && (
        <FinalHeader>
          <span>
            <Icon name="trophy" size={34} />
          </span>
          <h2>Ranking final</h2>
        </FinalHeader>
      )}

      {(open || finished) && (
        <ul style={{ flex: 1 }}>
          {ranking.map((player, position) => {
            const you = user_id === player.id;
            const waiting = !isDone(player.id);

            return (
              <PlayerRow
                key={player.id || player.name}
                $index={position}
                $you={you}
                $offline={!player.online}
                $rank={finished ? position : undefined}
              >
                {finished ? (
                  <Medal $place={position}>
                    {position === 0 ? (
                      <Icon name="crown" size={18} />
                    ) : (
                      position + 1
                    )}
                  </Medal>
                ) : (
                  <AvatarSlot $waiting={waiting}>
                    {player?.online ? (
                      <>
                        {waiting && (
                          <CircularProgress size={40} thickness={3} />
                        )}
                        <AnimalBg
                          circle
                          dance={waiting}
                          size="32px"
                          name={player.avatar as AnimalsEnum}
                          color={player.color}
                        />
                      </>
                    ) : (
                      <Icon name="offline" size={24} label="Offline" />
                    )}
                  </AvatarSlot>
                )}
                <PlayerName>
                  <span>{player.name}</span>
                  {you && (
                    <Tag $tone="teal">
                      <Icon name="you" size={12} strokeWidth={3} />
                      você
                    </Tag>
                  )}
                </PlayerName>
                <Points $big={finished && !position}>
                  {player.points || 0}
                </Points>
              </PlayerRow>
            );
          })}
        </ul>
      )}

      {finished && <ScoreChart players={ranking} />}

      {finished && (
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() =>
            navigate(PATHS.GAME.HOME(), {
              replace: true,
            })
          }
          startIcon={<Icon name="back" />}
        >
          Começar um novo jogo
        </Button>
      )}
    </BoardContainer>
  );
};

export default Board;
